import { withTranslation } from '@application/i18n/with_translation';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { Avatar, Spacer, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Participant } from '@feature/cases/common';
import { useService } from '@visiba/instantiation';
import styled from 'styled-components';

interface Props {
	host: Participant | null;
	participantAmount: number;
	hasMultipleOtherParticipants: boolean;
	receptionName: string;
	time?: VisibaDate;
	rfv?: string;
	representedPatient?: string;
	representative?: string;
}

const elements = {
	container: styled.div`
		display: flex;
	`,
	avatar: styled.div`
		display: flex;
		align-items: flex-start;
		width: 48px;
		flex-shrink: 0;
	`,
	textSections: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x1_5};
		max-width: 100%;
		width: 100%;

		${tokens.breakpointQuery.medium} {
			max-width: 70%;
		}
	`,
	textSection: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x0_5};
		max-width: 75%;
	`,
};

/**
 * Information about the case in written form.
 */
export function ExtensiveInfo({
	host,
	hasMultipleOtherParticipants,
	receptionName,
	representedPatient,
	representative,
	rfv,
	time,
	participantAmount,
}: Props): JSX.Element {
	const dateService = useService(DateService);
	const formattedDate = time?.format(
		dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
	);

	return (
		<elements.container>
			<elements.avatar>
				{hasMultipleOtherParticipants ? (
					<Avatar icon='group' size='large' />
				) : (
					<Avatar src={host?.profileImageUrl} name={host?.name ?? receptionName ?? ''} size='large' />
				)}
			</elements.avatar>

			<Spacer horizontal={tokens.spacing.x2} />

			{host != null ? (
				<elements.textSections>
					<TextSectionOne
						host={host}
						participantAmount={participantAmount}
						receptionName={receptionName}
						formattedDate={formattedDate}
					/>
					<TextSectionTwo representedPatient={representedPatient} representative={representative} rfv={rfv} />
				</elements.textSections>
			) : (
				<elements.textSections>
					<elements.textSection>
						<Text
							variant='headingXSmall'
							style={{
								color: tokens.color.content.default,
							}}
						>
							{receptionName}
						</Text>
					</elements.textSection>
					<TextSectionTwo representedPatient={representedPatient} representative={representative} />
				</elements.textSections>
			)}
		</elements.container>
	);
}

interface TextSectionOneProps {
	host: Participant;
	receptionName: string;
	participantAmount: number;
	formattedDate?: string;
}

function TextSectionOne({ host, receptionName, formattedDate, participantAmount }: TextSectionOneProps): JSX.Element {
	return (
		<elements.textSection>
			<Text variant='headingXSmall' style={{ color: tokens.color.content.default }}>
				{host.name} {participantAmount > 1 ? <span>+{participantAmount - 1 /* -1 because host is not counted */}</span> : null}
			</Text>
			<Text variant='labelRegular' style={{ color: tokens.color.content.default }}>
				{receptionName}
			</Text>
			{formattedDate != null ? (
				<Text variant='labelSemiBold' style={{ color: tokens.color.content.default }}>
					{formattedDate}
				</Text>
			) : null}
		</elements.textSection>
	);
}

interface TextSectionTwoProps {
	representedPatient?: string;
	representative?: string;
	rfv?: string;
}

function TextSectionTwo({ representedPatient, representative, rfv }: TextSectionTwoProps): JSX.Element | null {
	if (representedPatient == null && representative == null && rfv == null) return null;

	return (
		<elements.textSection>
			{rfv != null ? (
				<Text variant='labelRegular' style={{ color: tokens.color.content.muted }}>
					{rfv}
				</Text>
			) : null}
			{representedPatient != null ? (
				<Text variant='labelRegular' style={{ color: tokens.color.content.muted }}>
					<Text variant='labelSemiBold'>{withTranslation('PW.Component.CaseCard.Patient')}</Text> {representedPatient}
				</Text>
			) : null}
			{representative != null ? (
				<Text variant='labelRegular' style={{ color: tokens.color.content.muted }}>
					<Text variant='labelSemiBold'>{withTranslation('PW.Component.CaseCard.Representative')}</Text> {representative}
				</Text>
			) : null}
		</elements.textSection>
	);
}
